/**
 * Unified Authentication Hook for Edyt SaaS Platform
 * Consolidates useAuth, useAuthState, and useSmartAuth into a single, robust solution
 * Single source of truth for all authentication-related state and logic
 */

import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { authService } from '../lib/authService';

// Comprehensive authentication context interface
interface AuthContextType {
  // Core authentication state
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  
  // Token management and expiry
  token: string | null;
  tokenExpiryInfo: {
    isExpired: boolean;
    timeUntilExpiry: number;
  } | null;
  
  // Authentication functions
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: { displayName?: string; photoURL?: string }) => Promise<void>;
  
  // Token utility functions
  refreshToken: () => Promise<void>;
  isTokenExpiringSoon: (thresholdMinutes?: number) => boolean;
  getTokenExpiryTime: () => string;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Core authentication state
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Token state (integrated from useAuthState)
  const [token, setToken] = useState<string | null>(null);
  const [tokenExpiryInfo, setTokenExpiryInfo] = useState<{
    isExpired: boolean;
    timeUntilExpiry: number;
  } | null>(null);

  // Update token expiry info efficiently
  const updateTokenExpiryInfo = useCallback(() => {
    const expiryInfo = authService.getTokenExpiryInfo();
    setTokenExpiryInfo(expiryInfo);
  }, []);

  // Update all authentication state
  const updateAuthState = useCallback(async (firebaseUser: User | null) => {
    if (firebaseUser) {
      try {
        // Get fresh ID token
        const freshToken = await firebaseUser.getIdToken();
        
        // Store token and user data in our service
        authService.storeToken(freshToken);
        authService.storeUserData(firebaseUser);
        
        // Update local state
        setToken(freshToken);
        updateTokenExpiryInfo();
        
        console.log('Authentication state updated for user:', firebaseUser.email);
      } catch (error) {
        console.error('Failed to get user token:', error);
        setToken(null);
        setTokenExpiryInfo(null);
      }
    } else {
      // User signed out - clear all state
      setToken(null);
      setTokenExpiryInfo(null);
      console.log('User signed out, clearing authentication state');
    }
  }, [updateTokenExpiryInfo]);

  // Central Firebase auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        console.log('User signed in:', firebaseUser.email);
        await updateAuthState(firebaseUser);
      } else {
        // User is signed out
        console.log('User signed out, clearing authentication state');
        authService.clearAllAuthState();
        await updateAuthState(null);
      }
      
      setUser(firebaseUser);
      setLoading(false);
    });

    return unsubscribe;
  }, [updateAuthState]);

  // Set up periodic token expiry checks (efficient, no polling)
  useEffect(() => {
    if (!token) return;

    // Check token expiry every minute when token exists
    const interval = setInterval(() => {
      updateTokenExpiryInfo();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [token, updateTokenExpiryInfo]);

  // Authentication functions
  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sign in');
    }
  };

  const signUp = async (email: string, password: string, name?: string): Promise<void> => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      if (name && user) {
        await updateProfile(user, { displayName: name });
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create account');
    }
  };

  // Robust signOut function (from useAuth)
  const signOut = async (): Promise<void> => {
    try {
      console.log('Starting sign out process...');
      
      // 1. Call Firebase signOut method
      await firebaseSignOut(auth);
      
      // 2. Clear all authentication state (tokens, user data, etc.)
      authService.clearAllAuthState();
      
      // 3. Redirect to login page to force clean application state
      window.location.href = '/login';
      
      console.log('Sign out completed successfully');
    } catch (error: any) {
      console.error('Sign out error:', error);
      
      // Even if Firebase signOut fails, clear our state and redirect
      authService.clearAllAuthState();
      window.location.href = '/login';
      
      throw new Error(error.message || 'Failed to sign out');
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to send reset email');
    }
  };

  const updateUserProfile = async (data: { displayName?: string; photoURL?: string }): Promise<void> => {
    if (!user) throw new Error('No user logged in');
    
    try {
      await updateProfile(user, data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update profile');
    }
  };

  // Token utility functions (from useAuthState)
  const refreshToken = useCallback(async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const newToken = await currentUser.getIdToken(true); // Force refresh
        authService.storeToken(newToken);
        setToken(newToken);
        updateTokenExpiryInfo();
        console.log('Token refreshed successfully');
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
    }
  }, [updateTokenExpiryInfo]);

  const isTokenExpiringSoon = useCallback((thresholdMinutes: number = 10) => {
    if (!tokenExpiryInfo) return false;
    
    const thresholdMs = thresholdMinutes * 60 * 1000;
    return tokenExpiryInfo.timeUntilExpiry <= thresholdMs;
  }, [tokenExpiryInfo]);

  const getTokenExpiryTime = useCallback(() => {
    if (!tokenExpiryInfo) return 'No token';
    
    if (tokenExpiryInfo.isExpired) return 'Expired';
    
    const minutes = Math.floor(tokenExpiryInfo.timeUntilExpiry / (60 * 1000));
    const seconds = Math.floor((tokenExpiryInfo.timeUntilExpiry % (60 * 1000)) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }, [tokenExpiryInfo]);

  // Comprehensive context value
  const value: AuthContextType = {
    // Core state
    user,
    loading,
    isAuthenticated: !!user,
    
    // Token state
    token,
    tokenExpiryInfo,
    
    // Authentication functions
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateUserProfile,
    
    // Token utilities
    refreshToken,
    isTokenExpiringSoon,
    getTokenExpiryTime,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Main useAuth hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Simple useRequireAuth hook for components that need auth state
export const useRequireAuth = () => {
  const { user, loading } = useAuth();
  
  return { user, loading };
};

export default useAuth;
